/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import {
  Box,
  Button,
  Group,
  Popover,
  Progress,
  Skeleton,
  Stack,
  Text,
  rem,
  useMantineTheme,
} from '@mantine/core';
import { RiMoneyDollarCircleFill } from '@remixicon/react';
import { createQueryString } from '@toss/utils';
import { isNotNil } from 'es-toolkit';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Products } from '@fai/constants/domain-values';
import { useDedicatedFreeCredit } from '@fai/friendli-suite/features/products/dedicated-endpoints/use-credit-remaining';
import { useServerlessFreeCredit } from '@fai/friendli-suite/features/products/serverless-endpoints/plan/use-serverless-endpoints-team-plan';
import Paths from '@fai/friendli-suite/features/routes/paths';
import { usePaymentState } from '@fai/friendli-suite/features/settings/team-settings/use-payment';
import { useTeamRole } from '@fai/friendli-suite/features/settings/team-settings/use-role';
import { openAddPaymentModal } from '@fai/friendli-suite/views/team-settings/billing/payment-method/add-payment-modal';
import { TextVariants } from '@fai/ui/components/typography/variants';
import { InternalLinkWrapper } from '@fai/ui/views/common/link-wrapper';
import type { Nullish } from '@fai/util/types';

export function DedicatedFreeCreditRemaining() {
  const { teamId = '' } = useParams();
  const { isTeamOwner } = useTeamRole(teamId);
  const { used, total, remaining, loading } = useDedicatedFreeCredit(teamId);

  return (
    <FreeCreditRemainingPopover
      variant="dedicated"
      loading={loading}
      isTeamOwner={isTeamOwner}
      teamId={teamId}
      remaining={remaining}
      used={used}
      total={total}
    />
  );
}

export function ServerlessFreeCreditRemaining() {
  const { teamId = '' } = useParams();
  const { isTeamOwner } = useTeamRole(teamId);
  const { used, total, remaining, loading } = useServerlessFreeCredit(teamId);

  return (
    <FreeCreditRemainingPopover
      variant="serverless"
      loading={loading}
      isTeamOwner={isTeamOwner}
      teamId={teamId}
      remaining={remaining}
      used={used}
      total={total}
    />
  );
}

const FreeCreditRemainingPopover = ({
  variant,
  loading,
  isTeamOwner,
  teamId,
  remaining,
  used,
  total,
}: {
  variant: 'serverless' | 'dedicated';
  loading: boolean;
  isTeamOwner: boolean;
  teamId: string;
  remaining: Nullish<number>;
  used: Nullish<number>;
  total: Nullish<number>;
}) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { isPaymentEmpty } = usePaymentState(teamId);

  if (remaining === 0) {
    if (!isPaymentEmpty) return null;

    return (
      <Button variant="pill" size="sm" onClick={() => openAddPaymentModal()}>
        {t('suite.add_payment_method')}
      </Button>
    );
  }

  return (
    <Popover width={rem(312)} position="bottom-end" offset={4}>
      <Popover.Target>
        <Skeleton visible={loading} miw={rem(128)} w="fit-content">
          <Button variant="pill" size="sm" miw={rem(128)}>
            <Group gap={theme.pf.layout['4xs']} wrap="nowrap">
              <RiMoneyDollarCircleFill size={16} color={theme.pf.color.fg.neutral.strong} />
              {isNotNil(remaining) ? `$${remaining.toFixed(1)}` : '-'}

              <Text c={theme.pf.color.fg.neutral.subtle}>{t('shared.remaining')}</Text>
            </Group>
          </Button>
        </Skeleton>
      </Popover.Target>

      <Popover.Dropdown p={theme.pf.layout.xs} style={{ boxShadow: theme.pf.shadow['shadow-md'] }}>
        <DropDown
          variant={variant}
          isTeamOwner={isTeamOwner}
          teamId={teamId}
          remaining={remaining}
          used={used}
          total={total}
        />
      </Popover.Dropdown>
    </Popover>
  );
};

const DropDown = ({
  variant,
  isTeamOwner,
  teamId,
  remaining,
  used,
  total,
}: Pick<
  React.ComponentProps<typeof FreeCreditRemainingPopover>,
  'variant' | 'isTeamOwner' | 'teamId' | 'remaining' | 'total' | 'used'
>) => {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  return (
    <Stack c={theme.pf.color.fg.neutral.strong} gap={theme.pf.layout['2xs']}>
      <Text variant={TextVariants.TitleSm}>{t('shared.free_credit_remaining')}</Text>

      <Text variant={TextVariants.BodyMd}>
        <Trans
          i18nKey={
            variant === 'serverless'
              ? 'shared.serverless_free_credit_remaining_description'
              : 'shared.dedicated_free_credit_remaining_description'
          }
          values={{
            credits: isNotNil(remaining) ? remaining.toFixed(1) : '-',
          }}
          components={[
            isTeamOwner ? (
              <InternalLinkWrapper
                url={`${Paths.team(teamId).teamSettings.billing}${createQueryString(
                  variant === 'serverless'
                    ? { product: Products.ServerlessEndpoints }
                    : {
                        product: Products.DedicatedEndpoints,
                      }
                )}`}
                textVariant={TextVariants.TitleXs}
                icon={null}
              />
            ) : (
              <Box style={{ display: 'none' }} />
            ),
          ]}
        />
      </Text>

      {isNotNil(used) && isNotNil(total) && (
        <Stack gap={theme.pf.layout['4xs']}>
          <Progress
            bg={theme.pf.color.bg.neutral.disable.base}
            color={theme.pf.color.bg.cloudblue.strong.base}
            radius={theme.pf.radius.pill}
            size={rem(8)}
            styles={{ section: { borderRadius: theme.pf.radius.pill } }}
            value={(used * 100) / total}
          />

          <Text c={theme.pf.color.fg.neutral.default} variant={TextVariants.BodyXs}>
            {t('shared.free_credit_used', {
              usedCredits: used.toFixed(1),
              totalCredits: total.toFixed(0),
            })}
          </Text>
        </Stack>
      )}
    </Stack>
  );
};
